import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import './EditGasDistributorProjectModal.css';
import './EditMarketingCampaignProjectModal.css';
import AppIcon from './icons/AppIcon';

const ICON_OPTIONS = [
  'campaign', 'mail', 'send', 'groups', 'person', 'contacts',
  'analytics', 'trending_up', 'rocket_launch', 'star', 'flag',
];

const COLOR_OPTIONS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b',
  '#10b981', '#06b6d4', '#3b82f6', '#44e1ff', '#b43bea',
];

const WEEKDAY_OPTIONS = [
  { id: 'mon', label: 'Seg' },
  { id: 'tue', label: 'Ter' },
  { id: 'wed', label: 'Qua' },
  { id: 'thu', label: 'Qui' },
  { id: 'fri', label: 'Sex' },
  { id: 'sat', label: 'Sáb' },
  { id: 'sun', label: 'Dom' }
];

const buildAdvancedMessageDraft = () => ({
  id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  content: '',
  button_enabled: false,
  buttons: [{ id: `btn-${Date.now()}`, label: '' }],
  typing_enabled: false,
  typing_seconds: 0,
  image_enabled: false,
  image_url: '',
  audio_enabled: false,
  audio_url: '',
  audio_file_name: ''
});

/**
 * Edição de projeto Campanha de marketing — layout em abas (mesmo padrão do Gas Distributor).
 * Abas: Geral | Conexões | Formulário
 */
export default function EditMarketingCampaignProjectModal({ isOpen, onClose, project, onSave }) {
  const [activeTab, setActiveTab] = useState('geral');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('campaign');
  const [color, setColor] = useState('#6366f1');
  const [backgroundImage, setBackgroundImage] = useState('');

  const [connections, setConnections] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedConnectionIds, setSelectedConnectionIds] = useState([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [formEnabled, setFormEnabled] = useState(false);
  const [formFields, setFormFields] = useState([]);
  const [equipeVendasListId, setEquipeVendasListId] = useState(null);
  const [groupNotificationEnabled, setGroupNotificationEnabled] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [showAddSendListModal, setShowAddSendListModal] = useState(false);
  const [sendListName, setSendListName] = useState('');
  const [sendListDescription, setSendListDescription] = useState('');
  const [sendListFile, setSendListFile] = useState(null);
  const [sendLists, setSendLists] = useState([]);
  const [selectedListStatus, setSelectedListStatus] = useState(null);
  const [showListStatusModal, setShowListStatusModal] = useState(false);
  const [savingSendList, setSavingSendList] = useState(false);
  const [campaignsByListId, setCampaignsByListId] = useState({});
  const [showCampaignConfigModal, setShowCampaignConfigModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignSourceListId, setCampaignSourceListId] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    mode: 'simple',
    message_content: '',
    advanced_messages: [buildAdvancedMessageDraft()],
    delay_min: 10,
    delay_max: 30,
    campaign_start_date: '',
    campaign_end_date: '',
    start_time: '08:00',
    end_time: '18:00',
    weekdays: ['mon', 'tue', 'wed', 'thu', 'fri']
  });
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [uploadingMediaMessageId, setUploadingMediaMessageId] = useState(null);
  const [processingListId, setProcessingListId] = useState(null);
  const [verifyingListId, setVerifyingListId] = useState(null);
  const [campaignActionLoadingId, setCampaignActionLoadingId] = useState(null);
  const [openListMenuId, setOpenListMenuId] = useState(null);

  const loadData = useCallback(async () => {
    if (!project?.id) return;
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      if (!user?.company_id) return;

      setName(project.name || '');
      setDescription(project.description || '');
      setIcon(project.icon || 'campaign');
      setColor(project.color || '#6366f1');
      setBackgroundImage(project.background_image || '');

      const mc = project.settings?.marketing_campaign || {};
      setSelectedConnectionIds(Array.isArray(mc.connection_ids) ? [...mc.connection_ids] : []);
      setSelectedAgentIds(Array.isArray(mc.agent_ids) ? [...mc.agent_ids] : []);
      setFormEnabled(!!mc.useForm);
      setFormFields(Array.isArray(mc.formFields) ? [...mc.formFields] : []);

      const { data: connData, error: connErr } = await supabase
        .from('connections')
        .select('id, name, type, status, number, agente_id')
        .eq('company_id', user.company_id)
        .order('name');

      if (connErr) console.error(connErr);
      setConnections(connData || []);

      const { data: agentData, error: agentErr } = await supabase
        .from('agentes')
        .select('id, nome, chatbot_enabled, funcao_principal')
        .eq('empresa_id', user.company_id)
        .order('nome');

      if (agentErr) console.error(agentErr);
      setAgents(agentData || []);

      const { data: equipeList, error: equipeListErr } = await supabase
        .from('project_lists')
        .select('id, metadata')
        .eq('project_id', project.id)
        .eq('name', 'Equipe de Vendas')
        .maybeSingle();

      if (equipeListErr) console.error(equipeListErr);
      setEquipeVendasListId(equipeList?.id || null);

      const groupNotif = equipeList?.metadata?.group_notification || {};
      const isGroupEnabled = !!groupNotif.enabled;
      setGroupNotificationEnabled(isGroupEnabled);
      setSelectedGroup(
        groupNotif.group_jid && groupNotif.group_name
          ? { jid: groupNotif.group_jid, name: groupNotif.group_name }
          : null
      );

      const { data: sendListsData, error: sendListsError } = await supabase
        .from('marketing_contact_lists')
        .select('id, list_name, description, total_contacts, created_at')
        .eq('project_id', project.id)
        .order('created_at', { ascending: false });
      if (sendListsError) console.error(sendListsError);
      const normalizedSendLists = (sendListsData || []).map((list) => ({
        ...list,
        verification: { pending: list.total_contacts || 0, verified: 0, invalid: 0, error: 0, finished: false }
      }));
      setSendLists(normalizedSendLists);
      setTimeout(() => {
        refreshAllListsStatus(normalizedSendLists).catch((e) => console.error(e));
      }, 300);

      const { data: { session } } = await supabase.auth.getSession();
      const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      };
      const campaignsResponse = await fetch(`${BACKEND_NODE_URL}/api/campaigns`, { headers: authHeaders });
      if (campaignsResponse.ok) {
        const campaignsData = await campaignsResponse.json();
        const map = {};
        (campaignsData || []).forEach((camp) => {
          if (camp?.source_list_id) map[camp.source_list_id] = camp;
        });
        setCampaignsByListId(map);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [project]);

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Sessão expirada. Faça login novamente para continuar.');
    }
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`
    };
  };

  const refreshListStatus = async (listId) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_NODE_URL}/api/marketing-campaigns/lists/${listId}/status`, { headers });
    if (!response.ok) throw new Error('Erro ao buscar status da lista');
    return response.json();
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const refreshAllListsStatus = async (listsInput) => {
    const lists = listsInput || sendLists;
    if (!Array.isArray(lists) || lists.length === 0) return;
    const statusById = {};
    await Promise.all(lists.map(async (list) => {
      try {
        const status = await refreshListStatus(list.id);
        statusById[list.id] = status;
      } catch (e) {
        console.error(e);
      }
    }));
    setSendLists((prev) => prev.map((list) => {
      const s = statusById[list.id];
      return s ? { ...list, verification: s.verification } : list;
    }));
  };

  useEffect(() => {
    if (isOpen && project) {
      setActiveTab('geral');
      loadData();
    }
  }, [isOpen, project, loadData]);

  const toggleConnection = (id) => {
    setSelectedConnectionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAgent = (id) => {
    setSelectedAgentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const addFormField = () => {
    setFormFields((prev) => [
      ...prev,
      {
        id: `field-${Date.now()}`,
        name: '',
        required: false
      }
    ]);
  };

  const removeFormField = (fieldId) => {
    setFormFields((prev) => prev.filter((field) => field.id !== fieldId));
  };

  const updateFormField = (fieldId, updates) => {
    setFormFields((prev) =>
      prev.map((field) => (field.id === fieldId ? { ...field, ...updates } : field))
    );
  };

  const loadConnectionGroups = useCallback(async () => {
    if (!groupNotificationEnabled) {
      setAvailableGroups([]);
      return;
    }

    const connectionId = selectedConnectionIds[0];
    if (!connectionId) {
      setAvailableGroups([]);
      return;
    }

    setLoadingGroups(true);
    try {
      const response = await fetch(
        `${BACKEND_NODE_URL}/connections/groups/${connectionId}?force=true`,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) {
        throw new Error(`Erro HTTP ${response.status}`);
      }

      const data = await response.json();
      const groups = Array.isArray(data?.groups) ? data.groups : [];
      setAvailableGroups(groups);

      if (selectedGroup?.jid) {
        const existsInCurrentConnection = groups.some((group) => group.jid === selectedGroup.jid);
        if (!existsInCurrentConnection) setSelectedGroup(null);
      }
    } catch (error) {
      console.error('Erro ao carregar grupos da conexão:', error);
      setAvailableGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, [groupNotificationEnabled, selectedConnectionIds, selectedGroup]);

  const whatsappConnections = connections.filter((c) => c.type === 'whatsapp');

  const handleSaveSendList = async () => {
    if (!project?.id) return;
    if (!sendListName.trim()) return alert('Informe o nome da lista.');
    if (!sendListFile) return alert('Selecione um arquivo CSV.');

    try {
      setSavingSendList(true);
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        throw new Error('Sessão expirada. Faça login novamente.');
      }
      const formData = new FormData();
      formData.append('projectId', project.id);
      formData.append('listName', sendListName.trim());
      formData.append('description', sendListDescription.trim());
      formData.append('file', sendListFile);

      const response = await fetch(`${BACKEND_NODE_URL}/api/marketing-campaigns/upload-contact-list`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result?.error || 'Erro ao salvar lista');

      const { data: refreshedLists } = await supabase
        .from('marketing_contact_lists')
        .select('id, list_name, description, total_contacts, created_at')
        .eq('project_id', project.id)
        .order('created_at', { ascending: false });
      const normalizedLists = (refreshedLists || []).map((list) => ({
        ...list,
        verification: { pending: list.total_contacts || 0, verified: 0, invalid: 0, error: 0, finished: false }
      }));
      setSendLists(normalizedLists);

      // A verificação e sincronização da agenda já são executadas no backend
      // durante o upload da lista. Aqui apenas atualizamos o status exibido na UI.
      setTimeout(() => {
        refreshAllListsStatus(normalizedLists).catch((e) => console.error(e));
      }, 900);

      setShowAddSendListModal(false);
      setSendListName('');
      setSendListDescription('');
      setSendListFile(null);
      const verifiedCount = Number(result?.processing?.verification?.verified || 0);
      const agendaAdded = Number(result?.processing?.agenda?.added || 0);
      const agendaExists = Number(result?.processing?.agenda?.already_exists || 0);
      alert(
        `Lista salva com sucesso.\n` +
        `Contatos verificados: ${verifiedCount}\n` +
        `Agenda atualizada: ${agendaAdded} novos, ${agendaExists} já existentes.`
      );
    } catch (error) {
      console.error(error);
      alert(error.message || 'Erro ao salvar lista');
    } finally {
      setSavingSendList(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${BACKEND_NODE_URL}/api/marketing-campaigns/template-file`, '_blank');
  };

  const openCreateCampaignModal = (list) => {
    setEditingCampaign(null);
    setCampaignSourceListId(list.id);
    setCampaignForm({
      name: `Campanha - ${list.list_name}`,
      description: list.description || '',
      mode: 'simple',
      message_content: '',
      advanced_messages: [buildAdvancedMessageDraft()],
      delay_min: 10,
      delay_max: 30,
      campaign_start_date: '',
      campaign_end_date: '',
      start_time: '08:00',
      end_time: '18:00',
      weekdays: ['mon', 'tue', 'wed', 'thu', 'fri']
    });
    setShowCampaignConfigModal(true);
  };

  const openEditCampaignModal = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignSourceListId(campaign.source_list_id);
    setCampaignForm({
      name: campaign.name || '',
      description: campaign.description || '',
      mode: campaign?.message_config?.campaign_mode || 'simple',
      message_content: campaign?.message_config?.content || '',
      advanced_messages: Array.isArray(campaign?.message_config?.advanced_messages) && campaign.message_config.advanced_messages.length > 0
        ? campaign.message_config.advanced_messages.map((msg, idx) => ({
          id: msg.id || `msg-${idx}-${Date.now()}`,
          content: msg.content || '',
          button_enabled: !!msg.button_enabled,
          buttons: Array.isArray(msg.buttons) && msg.buttons.length > 0
            ? msg.buttons.map((btn, bIdx) => ({
              id: btn.id || `btn-${idx}-${bIdx}-${Date.now()}`,
              label: btn.label || ''
            }))
            : [{ id: `btn-${idx}-${Date.now()}`, label: '' }],
          typing_enabled: !!msg.typing_enabled,
          typing_seconds: Number(msg.typing_seconds || 0),
          image_enabled: !!msg.image_enabled,
          image_url: msg.image_url || '',
          audio_enabled: !!msg.audio_enabled,
          audio_url: msg.audio_url || '',
          audio_file_name: msg.audio_file_name || ''
        }))
        : [buildAdvancedMessageDraft()],
      delay_min: campaign.delay_min || 10,
      delay_max: campaign.delay_max || 30,
      campaign_start_date: campaign?.scheduling?.campaign_start_date || '',
      campaign_end_date: campaign?.scheduling?.campaign_end_date || '',
      start_time: campaign?.scheduling?.daily_start_time || '08:00',
      end_time: campaign?.scheduling?.daily_end_time || '18:00',
      weekdays: Array.isArray(campaign?.scheduling?.weekdays) && campaign.scheduling.weekdays.length > 0
        ? campaign.scheduling.weekdays
        : ['mon', 'tue', 'wed', 'thu', 'fri']
    });
    setShowCampaignConfigModal(true);
  };

  const handleSaveCampaign = async () => {
    if (!project?.id || !campaignSourceListId) return;
    if (!campaignForm.name.trim()) return alert('Informe o nome da campanha.');
    if (campaignForm.mode === 'simple' && !campaignForm.message_content.trim()) {
      return alert('Informe a mensagem da campanha simples.');
    }
    const advancedMessages = (campaignForm.advanced_messages || [])
      .map((msg) => ({
        ...msg,
        content: String(msg.content || '').trim(),
        image_url: String(msg.image_url || '').trim(),
        audio_url: String(msg.audio_url || '').trim(),
        buttons: Array.isArray(msg.buttons)
          ? msg.buttons
            .map((btn) => ({ ...btn, label: String(btn.label || '').trim() }))
            .filter((btn) => btn.label)
          : []
      }))
      .filter((msg) => msg.content || (msg.image_enabled && msg.image_url) || (msg.audio_enabled && msg.audio_url));
    if (campaignForm.mode === 'advanced' && advancedMessages.length === 0) {
      return alert('Adicione ao menos uma frase no modo personalizado.');
    }
    try {
      setSavingCampaign(true);
      const { data: { session } } = await supabase.auth.getSession();
      const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      };
      const payload = {
        name: campaignForm.name.trim(),
        description: campaignForm.description.trim() || null,
        project_id: project.id,
        source_list_id: campaignSourceListId,
        connection_id: selectedConnectionIds[0] || null,
        message_config: {
          type: 'text',
          campaign_mode: campaignForm.mode === 'advanced' ? 'advanced' : 'simple',
          message_type: 'manual',
          content_type: 'text',
          content: campaignForm.message_content.trim(),
          advanced_messages: advancedMessages.map((msg, idx) => ({
            id: msg.id || `msg-${idx + 1}`,
            type: 'text',
            content: msg.content,
            button_enabled: !!msg.button_enabled,
            buttons: msg.button_enabled ? (msg.buttons || []) : [],
            typing_enabled: !!msg.typing_enabled,
            typing_seconds: Number(msg.typing_seconds || 0),
            image_enabled: !!msg.image_enabled,
            image_url: msg.image_enabled ? msg.image_url : '',
            audio_enabled: !!msg.audio_enabled,
            audio_url: msg.audio_enabled ? msg.audio_url : '',
            audio_file_name: msg.audio_file_name || ''
          })),
          delayMin: Number(campaignForm.delay_min || 10),
          delayMax: Number(campaignForm.delay_max || 30),
          linkPreview: false
        },
        scheduling: {
          campaign_start_date: campaignForm.campaign_start_date || null,
          campaign_end_date: campaignForm.campaign_end_date || null,
          daily_start_time: campaignForm.start_time,
          daily_end_time: campaignForm.end_time,
          weekdays: Array.isArray(campaignForm.weekdays) ? campaignForm.weekdays : [],
          auto_finish_when_list_ends: true,
          paused: true
        },
        delay_min: Number(campaignForm.delay_min || 10),
        delay_max: Number(campaignForm.delay_max || 30)
      };
      const endpoint = editingCampaign
        ? `${BACKEND_NODE_URL}/api/campaigns/${editingCampaign.id}`
        : `${BACKEND_NODE_URL}/api/campaigns`;
      const method = editingCampaign ? 'PUT' : 'POST';
      const response = await fetch(endpoint, {
        method,
        headers: authHeaders,
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.error || 'Erro ao salvar campanha');

      let finalCampaign = result;
      // Nova campanha: cria na API externa e já pausa imediatamente
      // para evitar recriação no próximo "Ativar campanha".
      if (!editingCampaign?.id && result?.id) {
        const activateResponse = await fetch(`${BACKEND_NODE_URL}/api/campaigns/${result.id}/activate`, {
          method: 'POST',
          headers: authHeaders
        });
        const activateResult = await activateResponse.json();
        if (!activateResponse.ok) {
          throw new Error(activateResult?.error || 'Erro ao criar campanha na API de envio');
        }

        const pauseResponse = await fetch(`${BACKEND_NODE_URL}/api/campaigns/${result.id}/pause`, {
          method: 'POST',
          headers: authHeaders
        });
        const pauseResult = await pauseResponse.json();
        if (!pauseResponse.ok) {
          throw new Error(pauseResult?.error || 'Campanha criada na API, mas falhou ao pausar automaticamente');
        }
        finalCampaign = pauseResult?.campaign || activateResult?.campaign || result;
      }

      setCampaignsByListId((prev) => ({
        ...prev,
        [campaignSourceListId]: finalCampaign
      }));
      setShowCampaignConfigModal(false);
      setEditingCampaign(null);
      alert(editingCampaign ? 'Campanha atualizada com sucesso.' : 'Campanha criada e pausada com sucesso.');
    } catch (error) {
      console.error(error);
      alert(error.message || 'Erro ao salvar campanha');
    } finally {
      setSavingCampaign(false);
    }
  };

  const openListStatusModal = async (list) => {
    try {
      setProcessingListId(list.id);
      const statusData = await refreshListStatus(list.id);
      setSelectedListStatus(statusData);
      setShowListStatusModal(true);
    } catch (e) {
      console.error(e);
      alert('Erro ao carregar detalhes da lista');
    } finally {
      setProcessingListId(null);
    }
  };

  const handleRunListVerification = async (list) => {
    if (!list?.id) return;
    try {
      setVerifyingListId(list.id);
      const headers = await getAuthHeaders();
      const response = await fetch(`${BACKEND_NODE_URL}/api/marketing-campaigns/lists/${list.id}/verify`, {
        method: 'POST',
        headers
      });
      let result = {};
      try {
        result = await response.json();
      } catch (_) {
        result = {};
      }
      if (!response.ok) throw new Error(result?.error || 'Erro ao iniciar verificação');

      // Polling de progresso: backend processa contato por contato e atualiza a lista em tempo real.
      const maxRounds = 240; // ~8 minutos com 2s
      let lastStatus = null;
      for (let round = 0; round < maxRounds; round += 1) {
        const statusData = await refreshListStatus(list.id);
        lastStatus = statusData;

        setSendLists((prev) => prev.map((it) => (
          it.id === list.id
            ? { ...it, verification: statusData.verification }
            : it
        )));

        if (showListStatusModal && selectedListStatus?.id === list.id) {
          setSelectedListStatus(statusData);
        }

        const running = !!statusData?.processing?.running;
        const finished = !!statusData?.verification?.finished;
        if (!running && finished) break;
        // eslint-disable-next-line no-await-in-loop
        await wait(2000);
      }

      await refreshAllListsStatus();
      const v = lastStatus?.verification || {};
      alert(
        `Verificação finalizada.\n` +
        `Verificados: ${v.verified || 0}\n` +
        `Pendentes: ${v.pending || 0}\n` +
        `Inválidos: ${v.invalid || 0}\n` +
        `Erro: ${v.error || 0}`
      );
    } catch (error) {
      console.error(error);
      alert(error.message || 'Erro ao verificar lista');
    } finally {
      setVerifyingListId(null);
    }
  };

  const handleToggleCampaign = async (campaign) => {
    try {
      setCampaignActionLoadingId(campaign.id);
      const headers = await getAuthHeaders();
      const shouldActivate = campaign.status !== 'active';
      const endpoint = shouldActivate
        ? `${BACKEND_NODE_URL}/api/campaigns/${campaign.id}/activate`
        : `${BACKEND_NODE_URL}/api/campaigns/${campaign.id}/pause`;
      const response = await fetch(endpoint, { method: 'POST', headers });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.error || 'Erro ao alterar status da campanha');
      await loadData();
      alert(shouldActivate ? 'Campanha ativada com sucesso.' : 'Campanha pausada com sucesso.');
    } catch (e) {
      console.error(e);
      alert(e.message || 'Erro ao alterar status da campanha');
    } finally {
      setCampaignActionLoadingId(null);
    }
  };

  const handleDeleteSendList = async (list) => {
    const confirmed = window.confirm('VOCE TEM CERTEZA DISSO, ESTE COMANDO NAO PODE SER REVERTIDO');
    if (!confirmed) return;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BACKEND_NODE_URL}/api/marketing-campaigns/lists/${list.id}`, {
        method: 'DELETE',
        headers
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.error || 'Erro ao deletar lista');

      setSendLists((prev) => prev.filter((item) => item.id !== list.id));
      setCampaignsByListId((prev) => {
        const next = { ...prev };
        delete next[list.id];
        return next;
      });
      setOpenListMenuId(null);
      alert('Lista deletada com sucesso.');
    } catch (error) {
      console.error(error);
      alert(error.message || 'Erro ao deletar lista');
    }
  };

  const addAdvancedMessage = () => {
    setCampaignForm((prev) => ({
      ...prev,
      advanced_messages: [
        ...(prev.advanced_messages || []),
        buildAdvancedMessageDraft()
      ]
    }));
  };

  const updateAdvancedMessage = (id, updates) => {
    setCampaignForm((prev) => ({
      ...prev,
      advanced_messages: (prev.advanced_messages || []).map((msg) => (
        msg.id === id ? { ...msg, ...updates } : msg
      ))
    }));
  };

  const removeAdvancedMessage = (id) => {
    setCampaignForm((prev) => {
      const filtered = (prev.advanced_messages || []).filter((msg) => msg.id !== id);
      return {
        ...prev,
        advanced_messages: filtered.length > 0 ? filtered : [buildAdvancedMessageDraft()]
      };
    });
  };

  const toggleWeekday = (weekdayId) => {
    setCampaignForm((prev) => {
      const current = Array.isArray(prev.weekdays) ? prev.weekdays : [];
      const next = current.includes(weekdayId)
        ? current.filter((id) => id !== weekdayId)
        : [...current, weekdayId];
      return { ...prev, weekdays: next };
    });
  };

  const addMessageButton = (messageId) => {
    setCampaignForm((prev) => ({
      ...prev,
      advanced_messages: (prev.advanced_messages || []).map((msg) => (
        msg.id === messageId
          ? {
            ...msg,
            buttons: [
              ...(Array.isArray(msg.buttons) ? msg.buttons : []),
              { id: `btn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, label: '' }
            ]
          }
          : msg
      ))
    }));
  };

  const updateMessageButton = (messageId, buttonId, label) => {
    setCampaignForm((prev) => ({
      ...prev,
      advanced_messages: (prev.advanced_messages || []).map((msg) => (
        msg.id === messageId
          ? {
            ...msg,
            buttons: (msg.buttons || []).map((btn) => (
              btn.id === buttonId ? { ...btn, label } : btn
            ))
          }
          : msg
      ))
    }));
  };

  const removeMessageButton = (messageId, buttonId) => {
    setCampaignForm((prev) => ({
      ...prev,
      advanced_messages: (prev.advanced_messages || []).map((msg) => {
        if (msg.id !== messageId) return msg;
        const filtered = (msg.buttons || []).filter((btn) => btn.id !== buttonId);
        return {
          ...msg,
          buttons: filtered.length > 0 ? filtered : [{ id: `btn-${Date.now()}`, label: '' }]
        };
      })
    }));
  };

  const parseStorageRefFromPublicUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      const m = url.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
      if (m) return { bucket: m[1], path: m[2] };
      return null;
    } catch (_) {
      return null;
    }
  };

  const removeOldMediaIfAny = async (publicUrl) => {
    try {
      const ref = parseStorageRefFromPublicUrl(publicUrl);
      if (!ref) return;
      await supabase.storage.from(ref.bucket).remove([ref.path]);
    } catch (_) {}
  };

  const uploadAdvancedMediaFile = async (messageId, file, mediaType) => {
    if (!file || !messageId) return;
    try {
      setUploadingMediaMessageId(messageId);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      if (!user?.company_id) throw new Error('Usuário sem company_id');
      if (!project?.id) throw new Error('Projeto não identificado');

      if (mediaType === 'image') {
        const maxImageSize = 3 * 1024 * 1024;
        if (!file.type.startsWith('image/')) throw new Error('Arquivo inválido. Use imagem.');
        if (file.size > maxImageSize) throw new Error('Imagem muito grande. Limite: 3MB');
      }
      if (mediaType === 'audio') {
        const maxAudioSize = 10 * 1024 * 1024;
        if (!file.type.startsWith('audio/')) throw new Error('Arquivo inválido. Use áudio.');
        if (file.size > maxAudioSize) throw new Error('Áudio muito grande. Limite: 10MB');
      }

      const existing = (campaignForm.advanced_messages || []).find((m) => m.id === messageId);
      if (mediaType === 'image' && existing?.image_url) await removeOldMediaIfAny(existing.image_url);
      if (mediaType === 'audio' && existing?.audio_url) await removeOldMediaIfAny(existing.audio_url);

      const bucket = 'project-attachments';
      const ext = (file.name || '').includes('.') ? file.name.split('.').pop() : (mediaType === 'audio' ? 'ogg' : 'jpg');
      const filePath = `marketing-campaign-media/${user.company_id}/${project.id}/${messageId}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type || (mediaType === 'audio' ? 'audio/ogg' : 'image/jpeg')
        });
      if (uploadError) throw new Error(uploadError.message);

      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(filePath);
      const publicUrl = urlData?.publicUrl || '';
      if (!publicUrl) throw new Error('Falha ao obter URL pública da mídia');

      if (mediaType === 'audio') {
        updateAdvancedMessage(messageId, {
          audio_file_name: file.name,
          audio_url: publicUrl,
          audio_enabled: true
        });
      } else {
        updateAdvancedMessage(messageId, {
          image_url: publicUrl,
          image_enabled: true
        });
      }
    } catch (error) {
      console.error('Erro ao subir mídia da campanha:', error);
      alert(error.message || 'Erro ao subir mídia');
    } finally {
      setUploadingMediaMessageId(null);
    }
  };

  const handleAudioMessageFile = (messageId, file) => {
    if (!file) return;
    uploadAdvancedMediaFile(messageId, file, 'audio');
  };

  const handleImageMessageFile = (messageId, file) => {
    if (!file) return;
    uploadAdvancedMediaFile(messageId, file, 'image');
  };

  useEffect(() => {
    if (!isOpen) return;
    loadConnectionGroups();
  }, [isOpen, loadConnectionGroups]);

  const handleSave = async () => {
    if (!project?.id) return;
    const trimmedName = String(name || '').trim();
    if (!trimmedName) {
      alert('Informe o nome do projeto.');
      return;
    }
    if (groupNotificationEnabled && !selectedGroup?.jid) {
      alert('Selecione um grupo do WhatsApp para ativar o envio do formulário.');
      return;
    }

    try {
      setSaving(true);
      const nextSettings = {
        ...(project.settings || {}),
        marketing_campaign: {
          ...(project.settings?.marketing_campaign || {}),
          dynamic_config_pending:
            project.settings?.marketing_campaign?.dynamic_config_pending ?? true,
          connection_ids: selectedConnectionIds,
          agent_ids: selectedAgentIds,
          useForm: formEnabled,
          formFields: formFields.map((field) => ({
            id: field.id || `field-${Date.now()}`,
            name: String(field.name || '').trim(),
            icon: field.icon || 'label',
            required: !!field.required
          })).filter((field) => field.name)
        },
      };

      const { error } = await supabase
        .from('projects')
        .update({
          name: trimmedName,
          description: description || null,
          icon: icon || 'campaign',
          color: color || '#6366f1',
          background_image: backgroundImage || null,
          ai_agent_id: selectedAgentIds[0] || null,
          settings: nextSettings,
          updated_at: new Date().toISOString(),
        })
        .eq('id', project.id);

      if (error) throw error;

      if (equipeVendasListId) {
        const { data: listData, error: listError } = await supabase
          .from('project_lists')
          .select('metadata')
          .eq('id', equipeVendasListId)
          .single();

        if (listError) throw listError;

        const nextMetadata = {
          ...(listData?.metadata || {}),
          group_notification: {
            enabled: !!groupNotificationEnabled,
            group_jid: groupNotificationEnabled ? (selectedGroup?.jid || null) : null,
            group_name: groupNotificationEnabled ? (selectedGroup?.name || null) : null
          }
        };

        const { error: updateListError } = await supabase
          .from('project_lists')
          .update({
            metadata: nextMetadata,
            updated_at: new Date().toISOString()
          })
          .eq('id', equipeVendasListId);

        if (updateListError) throw updateListError;
      }

      alert('Projeto atualizado com sucesso!');
      onSave?.();
      onClose();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Erro ao salvar projeto');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content gas-distributor-edit-modal marketing-campaign-edit-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            <AppIcon name="campaign" />
            Editar projeto — Campanha de marketing
          </h2>
          <button type="button" className="btn-close" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="tabs-container">
          <button
            type="button"
            className={`tab-button ${activeTab === 'geral' ? 'active' : ''}`}
            onClick={() => setActiveTab('geral')}
          >
            <AppIcon name="tune" />
            Geral
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'conexoes' ? 'active' : ''}`}
            onClick={() => setActiveTab('conexoes')}
          >
            <AppIcon name="link" />
            Conexões
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'formulario' ? 'active' : ''}`}
            onClick={() => setActiveTab('formulario')}
          >
            <AppIcon name="description" />
            Formulário
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'listas_envios' ? 'active' : ''}`}
            onClick={() => setActiveTab('listas_envios')}
          >
            <AppIcon name="campaign" />
            Listas de envios
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-container">
              <AppIcon name="sync" className="spinning" />
              <p>Carregando dados...</p>
            </div>
          ) : (
            <>
              {activeTab === 'geral' && (
                <>
                  <div className="config-section">
                    <h3>
                      <AppIcon name="info" />
                      Informações do projeto
                    </h3>
                    <p className="section-description">
                      Nome, descrição e aparência do quadro no painel de projetos.
                    </p>

                    <div className="form-field-step">
                      <label>
                        <AppIcon name="label" />
                        Nome do projeto
                      </label>
                      <input
                        type="text"
                        className="input-step"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ex: Black Friday 2026"
                      />
                    </div>

                    <div className="form-field-step">
                      <label>
                        <AppIcon name="description" />
                        Descrição
                      </label>
                      <textarea
                        className="textarea-step"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Objetivo da campanha..."
                      />
                    </div>
                  </div>

                  <div className="config-section">
                    <h3>
                      <AppIcon name="palette" />
                      Ícone e cor
                    </h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                      {ICON_OPTIONS.map((ic) => (
                        <button
                          key={ic}
                          type="button"
                          onClick={() => setIcon(ic)}
                          title={ic}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            border:
                              icon === ic
                                ? '2px solid var(--primary-color, #44e1ff)'
                                : '1px solid rgba(49, 117, 177, 0.4)',
                            background: icon === ic ? 'rgba(68, 225, 255, 0.15)' : 'rgba(49, 117, 177, 0.1)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                          }}
                        >
                          <AppIcon name={ic} />
                        </button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {COLOR_OPTIONS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setColor(c)}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background: c,
                            border:
                              color === c ? '3px solid #fff' : '2px solid transparent',
                            cursor: 'pointer',
                          }}
                          title={c}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="config-section">
                    <h3>
                      <AppIcon name="image" />
                      Plano de fundo (URL)
                    </h3>
                    <div className="form-field-step">
                      <input
                        type="url"
                        className="input-step"
                        value={backgroundImage}
                        onChange={(e) => setBackgroundImage(e.target.value)}
                        placeholder="https://..."
                      />
                      {backgroundImage ? (
                        <div style={{ marginTop: 12 }}>
                          <img
                            src={backgroundImage}
                            alt=""
                            style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8 }}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'conexoes' && (
                <>
                  <div className="config-section">
                    <h3>
                      <AppIcon name="smartphone" />
                      Conexões WhatsApp
                    </h3>
                    <p className="section-description">
                      Selecione uma ou mais conexões utilizadas nesta campanha. Apenas conexões
                      WhatsApp aparecem aqui.
                    </p>
                    {whatsappConnections.length === 0 ? (
                      <div className="no-connection">
                        <AppIcon name="link_off" />
                        <p>Nenhuma conexão encontrada para a empresa.</p>
                      </div>
                    ) : (
                      <div className="groups-list">
                        {whatsappConnections.map((conn) => {
                          const selected = selectedConnectionIds.includes(conn.id);
                          const active = String(conn.status || '').toUpperCase() === 'CONECTADO';
                          return (
                            <div
                              key={conn.id}
                              className={`group-card ${selected ? 'selected' : ''}`}
                              onClick={() => toggleConnection(conn.id)}
                              style={{ opacity: active ? 1 : 0.65 }}
                            >
                              <AppIcon name="chat" style={{ color: '#25D366' }} />
                              <div className="group-info">
                                <span className="group-name">{conn.name}</span>
                                <span className="group-participants">
                                  {conn.number || conn.status || '—'}
                                </span>
                              </div>
                              {selected && (
                                <AppIcon name="check_circle" className="check-icon" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="config-section">
                    <h3>
                      <AppIcon name="smart_toy" />
                      Agentes de IA
                    </h3>
                    <p className="section-description">
                      Opcional. O primeiro da lista é usado como agente principal do projeto
                      (campo <strong>ai_agent_id</strong>).
                    </p>
                    {agents.length === 0 ? (
                      <div className="no-connection">
                        <AppIcon name="smart_toy" />
                        <p>Nenhum agente cadastrado.</p>
                      </div>
                    ) : (
                      <div className="groups-list">
                        {agents.map((ag) => {
                          const selected = selectedAgentIds.includes(ag.id);
                          return (
                            <div
                              key={ag.id}
                              className={`group-card ${selected ? 'selected' : ''}`}
                              onClick={() => toggleAgent(ag.id)}
                            >
                              <AppIcon name="smart_toy" />
                              <div className="group-info">
                                <span className="group-name">{ag.nome}</span>
                                <span className="group-participants">
                                  {ag.funcao_principal || (ag.chatbot_enabled ? 'Ativo' : 'Inativo')}
                                </span>
                              </div>
                              {selected && (
                                <AppIcon name="check_circle" className="check-icon" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === 'formulario' && (
                <>
                  <div className="config-section">
                    <h3>
                      <AppIcon name="description" />
                      Formulário do projeto
                    </h3>

                    <label className="toggle-option">
                      <input
                        type="checkbox"
                        checked={formEnabled}
                        onChange={(e) => setFormEnabled(e.target.checked)}
                      />
                      <span>
                        Ativar formulário dinâmico neste projeto
                      </span>
                    </label>

                    {formEnabled && (
                      <>
                        <div style={{ marginTop: 16, marginBottom: 12 }}>
                          <button type="button" className="btn-secondary" onClick={addFormField}>
                            <AppIcon name="add" />
                            Adicionar campo
                          </button>
                        </div>

                        {formFields.length === 0 ? (
                          <div className="no-connection">
                            <AppIcon name="info" />
                            <p>Nenhum campo criado. Adicione ao menos 1 campo.</p>
                          </div>
                        ) : (
                          <div className="steps-container mc-steps-container">
                            {formFields.map((field, index) => (
                              <div key={field.id || `${index}-${field.name}`} className="step-item mc-step-item">
                                <div className="step-header mc-step-header">
                                  <div className="step-number">{index + 1}</div>
                                  <input
                                    type="text"
                                    className="input-step"
                                    value={field.name || ''}
                                    onChange={(e) => updateFormField(field.id, { name: e.target.value })}
                                    placeholder="Nome do campo (ex: Renda mensal)"
                                  />
                                  <button
                                    type="button"
                                    className="mc-btn-remove"
                                    onClick={() => removeFormField(field.id)}
                                  >
                                    <AppIcon name="delete" />
                                  </button>
                                </div>

                                <label className="checkbox-step">
                                  <input
                                    type="checkbox"
                                    checked={!!field.required}
                                    onChange={(e) => updateFormField(field.id, { required: e.target.checked })}
                                  />
                                  <span>Campo obrigatório</span>
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="config-section">
                    <h3>
                      <AppIcon name="groups" />
                      Enviar formulário para grupo do WhatsApp
                    </h3>

                    <label className="toggle-option">
                      <input
                        type="checkbox"
                        checked={groupNotificationEnabled}
                        onChange={(e) => setGroupNotificationEnabled(e.target.checked)}
                      />
                      <span>Ativar envio para grupo</span>
                    </label>

                    {groupNotificationEnabled && (
                      <div style={{ marginTop: 16 }}>
                        {selectedConnectionIds.length === 0 && (
                          <div className="no-connection">
                            <AppIcon name="warning" />
                            <p>Selecione ao menos uma conexão na aba "Conexões" para carregar os grupos.</p>
                          </div>
                        )}

                        {selectedConnectionIds.length > 0 && (
                          <>
                            <div style={{ marginBottom: 10 }}>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={loadConnectionGroups}
                                disabled={loadingGroups}
                              >
                                {loadingGroups ? (
                                  <>
                                    <AppIcon name="sync" className="spinning" />
                                    Carregando grupos...
                                  </>
                                ) : (
                                  <>
                                    <AppIcon name="sync" />
                                    Carregar grupos
                                  </>
                                )}
                              </button>
                            </div>

                            {availableGroups.length === 0 ? (
                              <div className="no-connection">
                                <AppIcon name="groups" />
                                <p>Nenhum grupo encontrado para a conexão selecionada.</p>
                              </div>
                            ) : (
                              <div className="groups-list">
                                {availableGroups.map((group) => {
                                  const selected = selectedGroup?.jid === group.jid;
                                  return (
                                    <div
                                      key={group.jid}
                                      className={`group-card ${selected ? 'selected' : ''}`}
                                      onClick={() => setSelectedGroup({ jid: group.jid, name: group.name || group.jid })}
                                    >
                                      <AppIcon name="groups" />
                                      <div className="group-info">
                                        <span className="group-name">{group.name || 'Grupo sem nome'}</span>
                                        <span className="group-participants">{group.jid}</span>
                                      </div>
                                      {selected && <AppIcon name="check_circle" className="check-icon" />}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === 'listas_envios' && (
                <>
                  <div className="config-section">
                    <h3>
                      <AppIcon name="campaign" />
                      Listas de envios
                    </h3>

                    <div className="mc-send-list-header">
                      <h4>Estrutura fixa da feature</h4>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setShowAddSendListModal(true)}
                      >
                        <AppIcon name="add" />
                        Adicionar lista
                      </button>
                    </div>

                    <div className="mc-template-box">
                      <strong>Destino dos contatos</strong>
                      <span>Sempre serão criados cards na lista <b>Disparo pendente</b>.</span>
                    </div>

                    <div className="mc-template-box">
                      <strong>Padrão de arquivo aceito (fixo)</strong>
                      <code>nome,telefone,email</code>
                      <span>- Obrigatórios: <b>nome</b> e <b>telefone</b></span>
                      <span>- Opcional: <b>email</b></span>
                      <span>- Colunas extras serão guardadas no card como dados complementares.</span>
                    </div>

                    {sendLists.length > 0 && (
                      <div className="mc-send-list-cards">
                        {sendLists.map((list) => (
                          <div key={list.id} className="campaign-card marketing-list-card">
                            <div className="campaign-card-header">
                              <div>
                                <h4 className="campaign-card-title">{list.list_name}</h4>
                                <p className="campaign-card-description">{list.description || 'Lista de envios'}</p>
                              </div>
                              <div className="mc-list-menu-container">
                                <button
                                  type="button"
                                  className="mc-list-menu-trigger"
                                  onClick={() => setOpenListMenuId((prev) => (prev === list.id ? null : list.id))}
                                  aria-label="Abrir menu da lista"
                                >
                                  <AppIcon name="more_vert" />
                                </button>
                                {openListMenuId === list.id && (
                                  <div className="mc-list-menu-dropdown">
                                    <button
                                      type="button"
                                      className="mc-list-menu-item danger"
                                      onClick={() => handleDeleteSendList(list)}
                                    >
                                      <AppIcon name="delete" />
                                      Deletar lista
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="campaign-card-info">
                              <div className="campaign-card-info-row">
                                <AppIcon name="people" />
                                <span>{list.total_contacts || 0} contatos</span>
                              </div>
                              <div className="campaign-card-info-row">
                                <AppIcon name="verified" />
                                <span>
                                  {list.verification?.verified || 0} verificados / {list.total_contacts || 0}
                                </span>
                              </div>
                              <div className="campaign-card-info-row">
                                <AppIcon name="calendar_today" />
                                <span>{list.created_at ? new Date(list.created_at).toLocaleDateString('pt-BR') : '-'}</span>
                              </div>
                            </div>
                            <div className="campaign-card-actions" style={{ marginTop: 8 }}>
                              <button type="button" className="btn-secondary" onClick={() => openListStatusModal(list)} disabled={processingListId === list.id}>
                                <AppIcon name="visibility" />
                                {processingListId === list.id ? 'Abrindo...' : 'Ver lista'}
                              </button>
                              {!campaignsByListId[list.id] ? (
                                list.verification?.finished ? (
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => openCreateCampaignModal(list)}
                                  >
                                    <AppIcon name="add" />
                                    Nova campanha
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => handleRunListVerification(list)}
                                    disabled={verifyingListId === list.id}
                                  >
                                    <AppIcon name="sync" />
                                    {verifyingListId === list.id ? 'Verificando...' : 'Iniciar verificação'}
                                  </button>
                                )
                              ) : (
                                <>
                                  <button type="button" className="btn-secondary" onClick={() => openEditCampaignModal(campaignsByListId[list.id])}>
                                    <AppIcon name="edit" />
                                    Editar campanha
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => handleToggleCampaign(campaignsByListId[list.id])}
                                    disabled={campaignActionLoadingId === campaignsByListId[list.id].id}
                                  >
                                    <AppIcon name={campaignsByListId[list.id].status === 'active' ? 'pause' : 'play_arrow'} />
                                    {campaignActionLoadingId === campaignsByListId[list.id].id
                                      ? 'Processando...'
                                      : campaignsByListId[list.id].status === 'active' ? 'Desativar campanha' : 'Ativar campanha'}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={loading || saving}>
            {saving ? (
              <>
                <AppIcon name="sync" className="spinning" />
                Salvando...
              </>
            ) : (
              <>
                <AppIcon name="save" />
                Salvar
              </>
            )}
          </button>
        </div>
      </div>

      {showAddSendListModal && (
        <div
          className="mc-send-modal-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setShowAddSendListModal(false);
          }}
        >
          <div className="mc-send-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mc-send-modal-header">
              <h3>
                <AppIcon name="add" />
                Adicionar lista de envios
              </h3>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowAddSendListModal(false)}
              >
                <AppIcon name="close" />
              </button>
            </div>

            <div className="mc-send-modal-body">
              <div className="form-field-step">
                <label>
                  <AppIcon name="label" />
                  Nome da lista
                </label>
                <input
                  type="text"
                  className="input-step"
                  value={sendListName}
                  onChange={(e) => setSendListName(e.target.value)}
                  placeholder="Ex: Leads Abril 2026"
                />
              </div>

              <div className="form-field-step">
                <label>
                  <AppIcon name="description" />
                  Descrição (opcional)
                </label>
                <textarea
                  className="textarea-step"
                  rows={2}
                  value={sendListDescription}
                  onChange={(e) => setSendListDescription(e.target.value)}
                  placeholder="Observação interna da lista"
                />
              </div>

              <div className="form-field-step">
                <label>
                  <AppIcon name="upload_file" />
                  Arquivo da lista
                </label>
                <input
                  type="file"
                  className="input-step"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setSendListFile(e.target.files?.[0] || null)}
                />
                <small style={{ color: '#fff' }}>
                  Arquivos aceitos: <b>.csv</b>, <b>.xlsx</b>, <b>.xls</b>
                </small>
                {sendListFile && (
                  <small style={{ color: '#fff' }}>
                    Arquivo selecionado: <b>{sendListFile.name}</b>
                  </small>
                )}
              </div>

              <div className="mc-template-box">
                <strong>Padrão obrigatório da planilha</strong>
                <code>nome,telefone,email</code>
                <span>- Obrigatório: <b>nome</b> e <b>telefone</b></span>
                <span>- Opcional: <b>email</b></span>
                <span>- O sistema sempre direciona os cards para <b>Disparo pendente</b>.</span>
              </div>

              <div className="mc-template-box">
                <strong>Exemplo de linha válida</strong>
                <code>Maria Souza,5521999999999,maria@email.com</code>
              </div>

              <div className="mc-template-actions">
                <button type="button" className="btn-secondary" onClick={handleDownloadTemplate}>
                  <AppIcon name="download" />
                  Baixar arquivo modelo (.xlsx)
                </button>
              </div>
            </div>

            {savingSendList && (
              <div className="mc-template-box" style={{ marginTop: 12 }}>
                <strong>Processando lista...</strong>
                <span>
                  Estamos verificando os números no WhatsApp e adicionando os contatos verificados
                  na agenda. Esse processo pode levar alguns minutos para listas maiores.
                </span>
              </div>
            )}

            <div className="mc-send-modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowAddSendListModal(false)}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={handleSaveSendList} disabled={savingSendList}>
                <AppIcon name="save" />
                {savingSendList ? 'Salvando...' : 'Salvar lista'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCampaignConfigModal && (
        <div
          className="mc-send-modal-overlay"
          onClick={(e) => {
            // Não fechar ao clicar fora para evitar perda acidental da configuração
            e.stopPropagation();
          }}
        >
          <div className="mc-send-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mc-send-modal-header">
              <h3>
                <AppIcon name="campaign" />
                {editingCampaign ? 'Editar campanha' : 'Nova campanha'}
              </h3>
              <button type="button" className="btn-close" onClick={() => setShowCampaignConfigModal(false)}>
                <AppIcon name="close" />
              </button>
            </div>
            <div className="mc-send-modal-body">
              <div className="form-field-step">
                <label>Nome</label>
                <input className="input-step" value={campaignForm.name} onChange={(e) => setCampaignForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-field-step">
                <label>Descrição</label>
                <textarea className="textarea-step" rows={2} value={campaignForm.description} onChange={(e) => setCampaignForm((p) => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="form-field-step">
                <label>Tipo de envio</label>
                <select
                  className="input-step"
                  value={campaignForm.mode}
                  onChange={(e) => setCampaignForm((p) => ({ ...p, mode: e.target.value }))}
                >
                  <option value="simple">Simples</option>
                  <option value="advanced">Avançada (agendamento em massa)</option>
                </select>
              </div>
              <div className="form-field-step">
                {campaignForm.mode === 'simple' ? (
                  <>
                    <label>Mensagem (simples)</label>
                    <textarea className="textarea-step" rows={4} value={campaignForm.message_content} onChange={(e) => setCampaignForm((p) => ({ ...p, message_content: e.target.value }))} />
                  </>
                ) : (
                  <>
                    <label>Frases da campanha personalizada</label>
                    <small style={{ color: '#fff', display: 'block', marginBottom: 8 }}>
                      Configure abaixo o planejamento de disparo em massa para o modo avançado.
                    </small>
                    <div className="mc-template-box" style={{ marginBottom: 10 }}>
                      <span><b>Período da campanha</b></span>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div className="form-field-step">
                          <label>Data início</label>
                          <input
                            type="date"
                            className="input-step"
                            value={campaignForm.campaign_start_date || ''}
                            onChange={(e) => setCampaignForm((p) => ({ ...p, campaign_start_date: e.target.value }))}
                          />
                        </div>
                        <div className="form-field-step">
                          <label>Data fim</label>
                          <input
                            type="date"
                            className="input-step"
                            value={campaignForm.campaign_end_date || ''}
                            onChange={(e) => setCampaignForm((p) => ({ ...p, campaign_end_date: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mc-template-box" style={{ marginBottom: 10 }}>
                      <span><b>Horário diário de disparo</b></span>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div className="form-field-step">
                          <label>Hora início</label>
                          <input
                            type="time"
                            className="input-step"
                            value={campaignForm.start_time}
                            onChange={(e) => setCampaignForm((p) => ({ ...p, start_time: e.target.value }))}
                          />
                        </div>
                        <div className="form-field-step">
                          <label>Hora fim</label>
                          <input
                            type="time"
                            className="input-step"
                            value={campaignForm.end_time}
                            onChange={(e) => setCampaignForm((p) => ({ ...p, end_time: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mc-template-box" style={{ marginBottom: 10 }}>
                      <span><b>Dias da semana habilitados</b></span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                        {WEEKDAY_OPTIONS.map((day) => {
                          const selected = (campaignForm.weekdays || []).includes(day.id);
                          return (
                            <button
                              key={day.id}
                              type="button"
                              className={selected ? 'btn-primary' : 'btn-secondary'}
                              onClick={() => toggleWeekday(day.id)}
                            >
                              {day.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(campaignForm.advanced_messages || []).map((msg, idx) => (
                        <div key={msg.id} className="mc-template-box" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <b>Mensagem {idx + 1}</b>
                            <button type="button" className="btn-secondary" onClick={() => removeAdvancedMessage(msg.id)}>
                              <AppIcon name="delete" />
                            </button>
                          </div>
                          <textarea
                            className="textarea-step"
                            rows={3}
                            value={msg.content || ''}
                            onChange={(e) => updateAdvancedMessage(msg.id, { content: e.target.value })}
                            placeholder={`Texto da mensagem ${idx + 1}`}
                          />
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={!!msg.button_enabled}
                                onChange={(e) => updateAdvancedMessage(msg.id, { button_enabled: e.target.checked })}
                              />
                              Habilitar botões
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={!!msg.typing_enabled}
                                onChange={(e) => updateAdvancedMessage(msg.id, { typing_enabled: e.target.checked })}
                              />
                              Habilitar timer digitando
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={!!msg.image_enabled}
                                onChange={(e) => updateAdvancedMessage(msg.id, { image_enabled: e.target.checked })}
                              />
                              Adicionar imagem
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={!!msg.audio_enabled}
                                onChange={(e) => updateAdvancedMessage(msg.id, { audio_enabled: e.target.checked })}
                              />
                              Adicionar áudio
                            </label>
                          </div>

                          {!!msg.typing_enabled && (
                            <div className="form-field-step">
                              <label>Tempo digitando (segundos)</label>
                              <input
                                type="number"
                                min="0"
                                className="input-step"
                                value={msg.typing_seconds || 0}
                                onChange={(e) => updateAdvancedMessage(msg.id, { typing_seconds: Number(e.target.value || 0) })}
                              />
                            </div>
                          )}

                          {!!msg.button_enabled && (
                            <div className="form-field-step">
                              <label>Botões da mensagem</label>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {(msg.buttons || []).map((btn) => (
                                  <div key={btn.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6 }}>
                                    <input
                                      className="input-step"
                                      value={btn.label || ''}
                                      onChange={(e) => updateMessageButton(msg.id, btn.id, e.target.value)}
                                      placeholder="Texto do botão"
                                    />
                                    <button type="button" className="btn-secondary" onClick={() => removeMessageButton(msg.id, btn.id)}>
                                      <AppIcon name="delete" />
                                    </button>
                                  </div>
                                ))}
                                <button type="button" className="btn-secondary" onClick={() => addMessageButton(msg.id)}>
                                  <AppIcon name="add" />
                                  Adicionar botão
                                </button>
                              </div>
                            </div>
                          )}

                          {!!msg.image_enabled && (
                            <div className="form-field-step">
                              <label>Imagem (URL)</label>
                              <input
                                className="input-step"
                                value={msg.image_url || ''}
                                onChange={(e) => updateAdvancedMessage(msg.id, { image_url: e.target.value })}
                                placeholder="https://..."
                              />
                              <input
                                type="file"
                                accept="image/*"
                                className="input-step"
                                onChange={(e) => handleImageMessageFile(msg.id, e.target.files?.[0])}
                                disabled={uploadingMediaMessageId === msg.id}
                              />
                              <small style={{ color: '#fff' }}>
                                {uploadingMediaMessageId === msg.id
                                  ? 'Enviando imagem para storage...'
                                  : 'Você pode usar URL direta ou enviar arquivo.'}
                              </small>
                            </div>
                          )}

                          {!!msg.audio_enabled && (
                            <div className="form-field-step">
                              <label>Áudio da mensagem</label>
                              <input
                                className="input-step"
                                value={msg.audio_url || ''}
                                onChange={(e) => updateAdvancedMessage(msg.id, { audio_url: e.target.value })}
                                placeholder="URL do áudio (ou selecione arquivo abaixo)"
                              />
                              <input
                                type="file"
                                accept="audio/*"
                                className="input-step"
                                onChange={(e) => handleAudioMessageFile(msg.id, e.target.files?.[0])}
                                disabled={uploadingMediaMessageId === msg.id}
                              />
                              <small style={{ color: '#fff' }}>
                                {uploadingMediaMessageId === msg.id
                                  ? 'Enviando áudio para storage...'
                                  : (msg.audio_file_name
                                    ? `Arquivo enviado: ${msg.audio_file_name}`
                                    : 'Você pode usar URL direta ou enviar arquivo.')}
                              </small>
                            </div>
                          )}
                        </div>
                      ))}
                      <button type="button" className="btn-secondary" onClick={addAdvancedMessage}>
                        <AppIcon name="add" />
                        Adicionar frase
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="mc-send-modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowCampaignConfigModal(false)}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={handleSaveCampaign} disabled={savingCampaign}>
                <AppIcon name="save" />
                {savingCampaign ? 'Salvando...' : 'Salvar campanha'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showListStatusModal && selectedListStatus && (
        <div className="mc-send-modal-overlay" onClick={() => setShowListStatusModal(false)}>
          <div className="mc-send-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mc-send-modal-header">
              <h3>
                <AppIcon name="visibility" />
                {selectedListStatus.list_name}
              </h3>
              <button type="button" className="btn-close" onClick={() => setShowListStatusModal(false)}>
                <AppIcon name="close" />
              </button>
            </div>
            <div className="mc-send-modal-body">
              <div className="mc-template-box">
                <span>Verificados: <b>{selectedListStatus.verification?.verified || 0}</b></span>
                <span>Pendentes: <b>{selectedListStatus.verification?.pending || 0}</b></span>
                <span>Inválidos: <b>{selectedListStatus.verification?.invalid || 0}</b></span>
                <span>Erro: <b>{selectedListStatus.verification?.error || 0}</b></span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                {(selectedListStatus.contacts_data || []).slice(0, 100).map((contact, idx) => (
                  <div key={`${contact.row_index || idx}-${contact.numero || idx}`} className="mc-template-box">
                    <span><b>{contact.nome || '-'}</b></span>
                    <span>{contact.numero || '-'}</span>
                    <span>{contact.email || '-'}</span>
                    <span>Status: <b>{contact.verification_status || 'pending'}</b></span>
                    {contact.verification_error ? (
                      <span>Erro verificação: <b>{contact.verification_error}</b></span>
                    ) : null}
                    {contact.agenda_contact_add_status ? (
                      <span>Agenda WhatsApp: <b>{contact.agenda_contact_add_status}</b></span>
                    ) : null}
                    {contact.agenda_contact_add_error ? (
                      <span>Erro agenda: <b>{contact.agenda_contact_add_error}</b></span>
                    ) : null}
                    {contact.contato_empresa_sync ? (
                      <span>Contato empresa: <b>{contact.contato_empresa_sync}</b></span>
                    ) : null}
                    {contact.contato_empresa_sync_error ? (
                      <span>Erro contato empresa: <b>{contact.contato_empresa_sync_error}</b></span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            <div className="mc-send-modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={async () => {
                  try {
                    await handleRunListVerification(selectedListStatus);
                  } catch (e) {
                    console.error(e);
                    alert('Erro ao reprocessar verificação');
                  }
                }}
              >
                <AppIcon name="sync" />
                Reprocessar verificação
              </button>
              <button type="button" className="btn-primary" onClick={() => setShowListStatusModal(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
